import { useMemo, useState } from "react";
import { Alert } from "../../../../../components/alert/Alert";

import {
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../../../components/modal/Dialog";
import {
  TableLegacy,
  TableLegacyContainer,
  TableLegacyTHead,
  TableLegacyTR,
  TableLegacyTH,
  TableLegacyTBody,
  TableLegacyTD,
} from "../../../../../components/table/TableLegacy";
import { apiV2AssertError } from "../../../../../hooks/api/v2/apiV2Request";
import { useProductPriceListBatchCreate } from "../../../../../hooks/api/v2/productPriceList/useProductPriceListBatchCreate";
import {
  ProductCreateRequestTypeProductItemType,
  ProductPriceListBatchCreateRequestType,
} from "@sellar-io/api-v2/types";
import { Button, InputContainer, InputCurrency } from "@sellar-io/components";
import { usePriceListBaseList } from "../../../../../hooks/api/v2/priceList/usePriceListBaseList";
import { twMerge } from "tailwind-merge";
import {
  formatPriceApiToInput,
  formatPriceInputToApi,
} from "../../../../../hooks/usePriceFormat";
import { formatCurrency, getDerivedPrice } from "@sellar-io/common";

type PriceListId = number;
type PriceListPricingMap = Record<
  PriceListId,
  {
    priceListId: number;
    baseListId: number | null;
    basePrice: number | null;
    price: number | null;
  }
>;

export default function ItemPricingUnbundledDialogContent({
  productId,
  onSubmit,
  loading,
  setUpdatedPriceListItems,
  containerType,
}: {
  loading: boolean;
  productId: number;
  onSubmit: () => void;
  setUpdatedPriceListItems: (
    item: ProductPriceListBatchCreateRequestType | undefined,
  ) => void;
  containerType?: ProductCreateRequestTypeProductItemType["containerType"];
}) {
  const productPriceListBatchCreateMutation = useProductPriceListBatchCreate();
  const { data: basePriceListsData, isFetched } = usePriceListBaseList({
    limit: 50,
  });

  const basePriceLists = useMemo(
    () => basePriceListsData?.pages.flatMap((page) => page) ?? [],
    [basePriceListsData],
  );

  const createPricingMap = () => {
    const newMap: PriceListPricingMap = {};
    basePriceLists.forEach((base) => {
      newMap[base.id] = {
        priceListId: base.id,
        baseListId: null,
        basePrice: null,
        price: null,
      };
      base.DerivedPriceLists.forEach((derivedList) => {
        newMap[derivedList.id] = {
          priceListId: derivedList.id,
          baseListId: base.id,
          basePrice: null,
          price: null,
        };
      });
    });
    return newMap;
  };
  const [pricingMap, setPricingMap] = useState(createPricingMap);
  const [previousBasePriceLists, setPreviousBasePriceLists] =
    useState(basePriceLists);

  if (isFetched && basePriceLists !== previousBasePriceLists) {
    setPreviousBasePriceLists(basePriceLists);
    setPricingMap(createPricingMap());
  }

  const onConfirm = () => {
    const updates = Object.entries(pricingMap)
      .filter(([, { price }]) => price !== null && price !== undefined)
      .map(([, pricing]) => ({
        PriceListId: pricing.priceListId,
        ProductId: productId,
        basePrice: pricing.basePrice,
        price: pricing.price ?? 0,
        discountable: true,
        active: true,
      }));
    setUpdatedPriceListItems(updates);

    onSubmit();
  };

  const handlePriceUpdate = (price: number | null, PriceListId: number) => {
    const newMap = { ...pricingMap };

    Object.entries(pricingMap)
      .filter(
        ([, { priceListId, baseListId }]) =>
          priceListId === PriceListId || baseListId === PriceListId,
      )
      .forEach(([, { priceListId, baseListId }]) => {
        const priceList = basePriceLists
          .find((list) => list.id === baseListId)
          ?.DerivedPriceLists.find((list) => list.id === priceListId);
        if (priceListId) {
          newMap[priceListId] = {
            ...newMap[priceListId],
            priceListId: priceListId,
            baseListId: baseListId ?? null,
            basePrice: baseListId ? price : null,
            price:
              baseListId && priceList
                ? getDerivedPrice({
                    basePrice: price ?? 0,
                    containerType: containerType ?? "keg",
                    productPriceTier: {
                      price,
                      derivationType: "inherit",
                      derivationValue: null,
                    },
                    priceTier: priceList,
                    priceTierContainerDiscounts:
                      priceList.PriceListContainerDiscounts,
                  })
                : price,
          };
        }
      });

    setPricingMap(newMap);
  };

  const noDerivedPriceLists = !basePriceLists.some((basePriceList) =>
    basePriceList.DerivedPriceLists.some(
      (derivedPriceList) => derivedPriceList,
    ),
  );

  return (
    <div>
      <DialogContent>
        <DialogNavigation
          title="Item pricing"
          description="Define your preferred pricing for this item."
        />

        <div className={"flex flex-col gap-8"}>
          {productPriceListBatchCreateMutation.isError ? (
            <Alert
              type={"error"}
              message={
                apiV2AssertError(productPriceListBatchCreateMutation.error)
                  ?.response?.data.message ?? "Unknown error"
              }
            />
          ) : null}

          <TableLegacyContainer className={"max-h-[300px]"}>
            <TableLegacy>
              <TableLegacyTHead>
                <TableLegacyTR className="text-xs font-semibold">
                  <TableLegacyTH className={"sticky top-0 w-1/2"}>
                    Base price list
                  </TableLegacyTH>
                  <TableLegacyTH className={"sticky top-0"}>
                    Unit price (ex. VAT)
                  </TableLegacyTH>
                </TableLegacyTR>
              </TableLegacyTHead>
              <TableLegacyTBody>
                {basePriceLists.map((basePriceList) => (
                  <TableLegacyTR key={basePriceList.id}>
                    <TableLegacyTD className="text-zinc-500">
                      {basePriceList.name}
                    </TableLegacyTD>
                    <TableLegacyTD>
                      <InputContainer className="w-full max-w-[200px]">
                        <InputCurrency
                          showPrefix
                          allowNegative
                          placeholder="0.00"
                          value={
                            pricingMap[basePriceList.id]?.price !== null &&
                            pricingMap[basePriceList.id]?.price !== undefined
                              ? formatPriceApiToInput(
                                  pricingMap[basePriceList.id]?.price ?? 0,
                                )
                              : undefined
                          }
                          onChange={(e) => {
                            const price = formatPriceInputToApi(e.target.value);

                            handlePriceUpdate(price, basePriceList.id);
                          }}
                        />
                      </InputContainer>
                    </TableLegacyTD>
                  </TableLegacyTR>
                ))}
              </TableLegacyTBody>
            </TableLegacy>
          </TableLegacyContainer>

          <TableLegacyContainer className={"max-h-[300px]"}>
            <TableLegacy>
              <TableLegacyTHead>
                <TableLegacyTR className="text-xs font-semibold">
                  <TableLegacyTH className={"sticky top-0 w-1/2"}>
                    Price list
                  </TableLegacyTH>
                  <TableLegacyTH className={"sticky top-0"}>
                    Base price
                  </TableLegacyTH>
                  <TableLegacyTH className={"sticky top-0"}>
                    Derived price
                  </TableLegacyTH>
                </TableLegacyTR>
              </TableLegacyTHead>
              <TableLegacyTBody>
                {basePriceLists.map((basePriceList) =>
                  basePriceList.DerivedPriceLists.map((priceList) => {
                    const basePrice = pricingMap[priceList.id]?.basePrice;
                    const price = pricingMap[priceList.id]?.price;
                    const hasBasePrice =
                      basePrice !== null && basePrice !== undefined;
                    const hasPrice = price !== null && price !== undefined;
                    return (
                      <TableLegacyTR key={priceList.id}>
                        <TableLegacyTD className="font-semibold">
                          {priceList.name}
                        </TableLegacyTD>
                        <TableLegacyTD
                          className={twMerge(
                            "text-zinc-500",
                            hasBasePrice && "line-through",
                          )}
                        >
                          {hasBasePrice ? formatCurrency(basePrice) : "£"}
                        </TableLegacyTD>
                        <TableLegacyTD>
                          {hasPrice
                            ? formatCurrency(
                                pricingMap[priceList.id]?.price ?? 0,
                              )
                            : "£"}
                        </TableLegacyTD>
                      </TableLegacyTR>
                    );
                  }),
                )}
              </TableLegacyTBody>
            </TableLegacy>
          </TableLegacyContainer>
          {noDerivedPriceLists && (
            <Alert
              type="information"
              title="No derived price lists defined"
              message="You can only add pricing to your derived price lists as your old migrated price tiers are uneditable. Replace your old price tiers with price lists to have this product's pricing appear in them"
            />
          )}
        </div>
      </DialogContent>
      <DialogCTA className="flex items-center justify-end">
        <Button
          loading={loading || productPriceListBatchCreateMutation.isPending}
          onClick={onConfirm}
        >
          Confirm
        </Button>
      </DialogCTA>
    </div>
  );
}
